import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

// What the office has told us we got wrong, on screen.
//
// WHY IT IS A SCREEN AND NOT JUST THE EXPORT. A report she cannot find again is
// one she has no reason to believe went anywhere — and the whole feature depends
// on her believing it did. "Where would I see it?" was the first question asked
// of it, which is the answer.
//
// IT LEADS WITH HER SENTENCE, not with the sheet id or the date. Everything else
// here is context we already had; her words are the only part that could not be
// recovered from anywhere else, and the only part worth scanning a list for.
//
// AND IT SHOWS WHAT WE SAID UNDERNEATH IT. A complaint without the claim it is
// about is unreadable a week later — "that was never renamed" means nothing
// unless the note that said it is right there.
//
// The drawings open on demand. They are 1-3 MB each and there are two per
// report; loading them into a list would be the footprint mistake this codebase
// has already paid for once.

interface ReportedProblem {
  id: string;
  createdAt: string;
  updatedAt: string | null;
  sheetId: string;
  address: string | null;
  jobNumber: string | null;
  kind: "Misdiagnosed" | "Missed";
  message: string;
  noteText: string | null;
  userId: string | null;
  hasDrawings: boolean;
}

export function ReportedProblemsSection() {
  const reports = useQuery<ReportedProblem[]>({
    queryKey: ["dashboard", "paa-reports"],
    queryFn: () => api<ReportedProblem[]>("/api/dashboard/paa-reports"),
  });

  if (reports.isLoading || reports.isError || !reports.data) return null;
  const data = reports.data;

  return (
    <section className="mt-8 overflow-hidden rounded-lg border border-rule bg-surface shadow-soft">
      <header className="border-b border-rule px-6 py-3.5">
        <div className="text-[0.95rem] font-semibold text-ink">Reported problems</div>
        <p className="mt-1 text-[0.82rem] leading-relaxed text-ink-muted">
          Sheets the office told us we read wrong, or changes we missed.
        </p>
      </header>

      {data.length === 0 ? (
        // Nothing reported is the normal state, and saying so plainly beats an
        // empty table that reads like a broken query.
        <p className="px-6 py-5 text-[0.88rem] leading-relaxed text-ink-soft">
          Nothing has been reported yet. On the review page, open a sheet’s
          “What we saw on this sheet” panel and use “Something here is wrong”.
        </p>
      ) : (
        <ul className="divide-y divide-rule">
          {data.map((r) => (
            <ReportRow key={r.id} report={r} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReportRow({ report }: { report: ReportedProblem }) {
  const [showDrawings, setShowDrawings] = useState(false);

  return (
    <li className="px-6 py-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-[0.84rem] font-medium text-ink">{report.sheetId}</span>
        <span
          className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[0.72rem] uppercase tracking-wide text-ink-muted"
        >
          {report.kind === "Missed" ? "missed" : "read wrong"}
        </span>
        <span className="min-w-0 flex-1 truncate text-[0.8rem] text-ink-muted">
          {[report.address, report.jobNumber].filter(Boolean).join(" · ")}
        </span>
        <span className="text-[0.78rem] text-ink-muted">
          {fmtDate(report.updatedAt ?? report.createdAt)}
          {report.updatedAt && " (edited)"}
        </span>
      </div>

      {/* Her sentence, first and largest. */}
      <p className="mt-2 text-[0.88rem] leading-relaxed text-ink">{report.message}</p>

      {report.noteText && (
        <p className="mt-1.5 text-[0.82rem] leading-relaxed text-ink-muted">
          <span className="uppercase tracking-wide text-[0.72rem]">We wrote</span>{" "}
          {report.noteText}
        </p>
      )}

      {report.hasDrawings ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowDrawings((o) => !o)}
            aria-expanded={showDrawings}
            className="text-[0.78rem] font-medium text-ink-muted underline decoration-rule-strong underline-offset-4 hover:text-primary hover:decoration-primary"
          >
            {showDrawings ? "Hide the drawings" : "Show the drawings"}
          </button>

          {showDrawings && (
            <div className="mt-2.5 grid gap-3 sm:grid-cols-2">
              <Drawing id={report.id} side="approved" label="Approved" />
              <Drawing id={report.id} side="updated" label="Updated" />
            </div>
          )}
        </div>
      ) : (
        // Stated, because a report with no drawings cannot be checked and that
        // changes how much weight it can carry — unlike the fact that a capture
        // SUCCEEDED, which tells a reader nothing they can act on.
        <p className="mt-2 text-[0.78rem] text-ink-muted">
          The comparison had expired, so no drawings were kept with this one.
        </p>
      )}
    </li>
  );
}

function Drawing({ id, side, label }: { id: string; side: string; label: string }) {
  const src = `/api/dashboard/paa-reports/${id}/image?side=${side}`;
  return (
    <figure className="min-w-0">
      <figcaption className="mb-1 text-[0.72rem] uppercase tracking-wide text-ink-muted">
        {label}
      </figcaption>
      {/* Opens full size in a tab — a 150 DPI plan sheet is unreadable at
          thumbnail size, and reading it is the entire point. */}
      <a href={src} target="_blank" rel="noreferrer">
        <img
          src={src}
          alt={`${label} sheet as it was when the problem was reported`}
          loading="lazy"
          className="w-full rounded-lg border border-rule bg-white"
        />
      </a>
    </figure>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
